import { Temporal } from "@js-temporal/polyfill";

import type { AddToCalendarButtonProps } from "./types";

export const blankEvent = {
  options: [
    "Apple",
    "Google",
    "iCal",
    "Microsoft365",
    "MicrosoftTeams",
    "Outlook.com",
    "Yahoo",
  ] as
    | (
        | "Apple"
        | "Google"
        | "iCal"
        | "Microsoft365"
        | "MicrosoftTeams"
        | "Outlook.com"
        | "Yahoo"
      )[]
    | undefined,
  buttonStyle: "text" as const,
  name: "Manual entry" as const,
  description: "" as const,
  location: "" as const,
  startDate: "today" as const,
  endDate: "" as const,
  startTime: "" as const,
  endTime: "" as const,
  timeZone: "" as const,
} as AddToCalendarButtonProps;

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const daysOfWeekTemporal = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export interface DateInfo {
  month: number;
  day: number;
  year: number;
  dayOfWeek: string;
  monthName: string;
  hour: number;
  minute: number;
}

export function getDateTimeInfo(
  dateString: string,
  timeString: string,
  timezone: string,
  userTimezone?: string,
): DateInfo | null {
  const timezonePattern = /^((?!unknown).)*$/;
  if (!timezonePattern.test(timezone)) {
    console.error("Invalid timezone, assuming America/Los_Angeles.");
    timezone = "America/Los_Angeles";
  }
  if (!userTimezone) {
    userTimezone = timezone;
  }
  if (!timezonePattern.test(userTimezone)) {
    console.error("Invalid userTimezone, assuming America/Los_Angeles.");
    userTimezone = "America/Los_Angeles";
  }

  const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
  if (!timePattern.test(timeString)) {
    console.log("Possibly invalid time format. Adding seconds...");
    timeString += ":00";
  }
  if (!timePattern.test(timeString)) {
    console.log("Invalid time format. Using default time.");
    timeString = "23:59:59";
  }

  const hasTime = timeString !== "";
  const zonedDateTime = Temporal.ZonedDateTime.from(
    `${dateString}${hasTime ? "T" : ""}${timeString}[${timezone}]`,
  );
  const userZonedDateTime = zonedDateTime.withTimeZone(
    userTimezone || timezone,
  );

  const dayOfWeek = daysOfWeekTemporal[userZonedDateTime.dayOfWeek - 1];
  if (!dayOfWeek) {
    console.error("Invalid dayOfWeek / date format. Use YYYY-MM-DD.");
    return null;
  }
  const monthName = monthNames[userZonedDateTime.month - 1];
  if (!monthName) {
    console.error("Invalid monthName / date format. Use YYYY-MM-DD.");
    return null;
  }
  const dateInfo = {
    month: userZonedDateTime.month,
    monthName: monthName,
    day: userZonedDateTime.day,
    year: userZonedDateTime.year,
    dayOfWeek: dayOfWeek,
    hour: userZonedDateTime.hour,
    minute: userZonedDateTime.minute,
  };

  return dateInfo;
}

export const SHOW_YEAR_THRESHOLD_MONTHS = 6;

export function shouldShowYear(dateInfo: DateInfo): boolean {
  const now = new Date();
  const monthsAhead =
    (dateInfo.year - now.getFullYear()) * 12 +
    (dateInfo.month - 1 - now.getMonth());
  return (
    monthsAhead > SHOW_YEAR_THRESHOLD_MONTHS ||
    (monthsAhead === SHOW_YEAR_THRESHOLD_MONTHS && dateInfo.day > now.getDate())
  );
}

export function getDateInfo(dateString: string): DateInfo | null {
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(dateString)) {
    console.error("Invalid date format. Use YYYY-MM-DD.");
    return null;
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    console.error("Invalid date.");
    return null;
  }

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const hour = date.getHours();
  const minute = date.getMinutes();

  const dayOfWeek = daysOfWeek[date.getDay()];
  if (!dayOfWeek) {
    console.error("Invalid dayOfWeek / date format. Use YYYY-MM-DD.");
    return null;
  }

  const monthName = monthNames[date.getMonth()];
  if (!monthName) {
    console.error("Invalid monthName / date format. Use YYYY-MM-DD.");
    return null;
  }

  return { month, monthName, day, year, dayOfWeek, hour, minute };
}
export function getDateInfoUTC(dateString: string): DateInfo | null {
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(dateString)) {
    console.error("Invalid date format. Use YYYY-MM-DD.");
    return null;
  }

  const date = new Date(`${dateString}T00:00:00Z`);

  if (isNaN(date.getTime())) {
    console.error("Invalid date.");
    return null;
  }

  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();

  const dayOfWeek = daysOfWeek[date.getUTCDay()];
  if (!dayOfWeek) {
    console.error("Invalid dayOfWeek / date format. Use YYYY-MM-DD.");
    return null;
  }

  const monthName = monthNames[date.getUTCMonth()];
  if (!monthName) {
    console.error("Invalid monthName / date format. Use YYYY-MM-DD.");
    return null;
  }

  return { month, monthName, day, year, dayOfWeek, hour, minute };
}

export function endsNextDayBeforeMorning(
  startDateInfo: DateInfo | null,
  endDateInfo: DateInfo | null,
) {
  if (!startDateInfo || !endDateInfo) {
    return false;
  }
  const isNextDay =
    (startDateInfo.month === endDateInfo.month &&
      startDateInfo.day === endDateInfo.day - 1) ||
    (startDateInfo.month !== endDateInfo.month && endDateInfo.day === 1);
  const isBeforeMorning = endDateInfo.hour < 6;
  return isNextDay && isBeforeMorning;
}

export function timeIsTomorrow(now: Date, startTime: Date): boolean {
  const normalizedNow = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  const normalizedStartTime = new Date(
    startTime.getFullYear(),
    startTime.getMonth(),
    startTime.getDate(),
  );

  const timeDifference =
    normalizedStartTime.getTime() - normalizedNow.getTime();

  const dayDifference = timeDifference / (1000 * 60 * 60 * 24);

  return dayDifference === 1;
}

export function eventTimesAreDefined(
  startTime: string | undefined,
  endTime: string | undefined,
) {
  return startTime !== undefined && endTime !== undefined;
}

export function spansMultipleDays(
  startDateInfo: DateInfo | null,
  endDateInfo: DateInfo | null,
) {
  if (!startDateInfo || !endDateInfo) {
    return false;
  }
  const notSameDay = startDateInfo.day !== endDateInfo.day;
  return notSameDay;
}

export function showMultipleDays(
  startDateInfo: DateInfo | null,
  endDateInfo: DateInfo | null,
) {
  if (!startDateInfo || !endDateInfo) {
    return false;
  }
  return (
    spansMultipleDays(startDateInfo, endDateInfo) &&
    !endsNextDayBeforeMorning(startDateInfo, endDateInfo)
  );
}

export function timeFormat(time?: string) {
  if (!time) {
    return "";
  }
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(":").map(Number);
  if (hours === undefined || minutes === undefined) {
    return "";
  }
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;
  return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
}

export function timeFormatDateInfo(dateInfo: DateInfo) {
  let hours = dateInfo.hour;
  const minutes = dateInfo.minute;
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;
  return `${hours}:${minutes.toString().padStart(2, "0")}${ampm}`;
}

export function formatRelativeTime(dateInfo: DateInfo): string {
  const now = new Date();
  const startDate = new Date(
    dateInfo.year,
    dateInfo.month - 1,
    dateInfo.day,
    dateInfo.hour,
    dateInfo.minute,
  );
  const difference = startDate.getTime() - now.getTime();
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor(difference / (1000 * 60));

  const isSameDay = dateInfo.day === now.getDate();
  const isSameMonth = dateInfo.month - 1 === now.getMonth();
  const isSameYear = dateInfo.year === now.getFullYear();
  const isToday = isSameDay && isSameMonth && isSameYear;
  const isTomorrow = timeIsTomorrow(now, startDate);

  if (difference < 0) {
    return "Happening now";
  }

  if (days === 0 && hours === 0) {
    return `Starts in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  if (days === 0 && hours < 1) {
    return `Starts in ${hours} hour${hours === 1 ? "" : "s"} ${minutes} minute${
      minutes === 1 ? "" : "s"
    }`;
  }
  if (isToday) {
    return `Starts in ~${hours} hour${hours === 1 ? "" : "s"}`;
  }
  if (isTomorrow) {
    return `Tomorrow`;
  }
  return ``;
}

export function getTimezoneAbbreviation(timezone: string): string {
  if (!timezone || timezone === "unknown" || timezone.trim() === "") {
    return "";
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(new Date());
    const timeZoneNamePart = parts.find((part) => part.type === "timeZoneName");
    return timeZoneNamePart?.value || "";
  } catch (error) {
    console.error(
      `Error getting timezone abbreviation for ${timezone}:`,
      error,
    );
    return "";
  }
}

export function getDateTimeInfoInTimezone(
  dateString: string,
  timeString: string,
  timezone: string,
): DateInfo | null {
  return getDateTimeInfo(dateString, timeString, timezone, timezone);
}

export function formatCompactTimeRangeWithEventTimezone(
  start: DateInfo,
  end: DateInfo,
  eventTimezoneStart?: DateInfo,
  eventTimezoneEnd?: DateInfo,
  timezoneAbbreviation?: string,
): { userTime: string; eventTime?: string } {
  const formatHour = (hour: number): string => {
    const h = hour % 12 || 12;
    return h.toString();
  };

  const formatMinute = (minute: number): string =>
    minute === 0 ? "" : `:${minute.toString().padStart(2, "0")}`;

  const startHour = formatHour(start.hour);
  const startMinute = formatMinute(start.minute);
  const endHour = formatHour(end.hour);
  const endMinute = formatMinute(end.minute);
  const startPeriod = start.hour < 12 ? "AM" : "PM";
  const endPeriod = end.hour < 12 ? "AM" : "PM";

  let timeRange: string;
  if (startPeriod === endPeriod) {
    timeRange = `${startHour}${startMinute}–${endHour}${endMinute}${startPeriod}`;
  } else {
    timeRange = `${startHour}${startMinute}${startPeriod}–${endHour}${endMinute}${endPeriod}`;
  }

  if (eventTimezoneStart && timezoneAbbreviation) {
    const eventStartHour = formatHour(eventTimezoneStart.hour);
    const eventStartMinute = formatMinute(eventTimezoneStart.minute);
    const eventStartPeriod = eventTimezoneStart.hour < 12 ? "AM" : "PM";

    let eventTimeRange: string;
    if (eventTimezoneEnd) {
      const eventEndHour = formatHour(eventTimezoneEnd.hour);
      const eventEndMinute = formatMinute(eventTimezoneEnd.minute);
      const eventEndPeriod = eventTimezoneEnd.hour < 12 ? "AM" : "PM";

      if (eventStartPeriod === eventEndPeriod) {
        eventTimeRange = `${eventStartHour}${eventStartMinute}–${eventEndHour}${eventEndMinute}${eventStartPeriod}`;
      } else {
        eventTimeRange = `${eventStartHour}${eventStartMinute}${eventStartPeriod}–${eventEndHour}${eventEndMinute}${eventEndPeriod}`;
      }
    } else {
      eventTimeRange = `${eventStartHour}${eventStartMinute}${eventStartPeriod}`;
    }

    return {
      userTime: timeRange,
      eventTime: `${eventTimeRange} ${timezoneAbbreviation}`,
    };
  }

  return { userTime: timeRange };
}

export function formatCompactTimeRange(
  start: DateInfo,
  end: DateInfo,
  eventTimezoneStart?: DateInfo,
  eventTimezoneEnd?: DateInfo,
  timezoneAbbreviation?: string,
): string {
  const result = formatCompactTimeRangeWithEventTimezone(
    start,
    end,
    eventTimezoneStart,
    eventTimezoneEnd,
    timezoneAbbreviation,
  );

  if (result.eventTime) {
    return `${result.userTime} (${result.eventTime})`;
  }

  return result.userTime;
}
